import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Youtube, Twitter } from 'lucide-react';
import { toast } from 'sonner';
import { useSite } from '../context/SiteContext';
import { api } from '../lib/api';

export default function Footer() {
    const { settings } = useSite();
    const [email, setEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [footerPages, setFooterPages] = useState([]);

    const social = settings?.social || {};
    const extraFooterPages = useMemo(
        () => footerPages.filter((p) => !['about', 'shipping', 'faq', 'contact'].includes(p.slug)),
        [footerPages]
    );

    useEffect(() => {
        api.get('/pages', { params: { footer_only: true } })
            .then((r) => setFooterPages(r.data || []))
            .catch(() => {});
    }, []);

    const onSubscribe = async (e) => {
        e.preventDefault();
        if (!email) return;
        setSubmitting(true);
        try {
            await api.post('/newsletter', { email });
            toast.success('You are on the list. Welcome to by anfal.');
            setEmail('');
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Could not subscribe. Please try again.';
            toast.error(typeof msg === 'string' ? msg : 'Could not subscribe.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <footer
            data-testid="public-footer"
            className="relative mt-10"
            style={{ backgroundColor: 'var(--hf-bg)', borderTop: '1px solid rgba(94,33,52,0.10)' }}
        >
            <div className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-10 py-12 lg:py-16">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
                    {/* Newsletter */}
                    <div className="lg:col-span-6">
                        <p className="eyebrow" style={{ color: 'var(--hf-text)' }}>Newsletter</p>
                        <h3 className="font-serif text-3xl md:text-4xl mt-2 shine-text">
                            {settings?.footer_newsletter_title || 'Stay Inspired'}
                        </h3>
                        <p className="mt-3 max-w-lg text-sm md:text-base" style={{ color: 'var(--hf-muted)' }}>
                            {settings?.footer_newsletter_subtitle}
                        </p>
                        <form onSubmit={onSubscribe} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md">
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Your email address"
                                data-testid="footer-newsletter-email-input"
                                className="input-luxury"
                            />
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="footer-newsletter-subscribe-button"
                                className="btn-burgundy px-5 py-2.5 rounded-xl text-xs tracking-[0.18em] uppercase font-medium whitespace-nowrap disabled:opacity-60"
                            >
                                {submitting ? 'Subscribing...' : 'Subscribe'}
                            </button>
                        </form>
                    </div>

                    {/* Links */}
                    <div className="lg:col-span-3">
                        <p className="eyebrow" style={{ color: 'var(--hf-text)' }}>Help</p>
                        <ul className="mt-4 space-y-3 text-sm" style={{ color: 'var(--hf-muted)' }}>
                            <li><Link to="/contact" data-testid="footer-link-contact" className="hover:text-[var(--hf-text)] transition-colors">Contact</Link></li>
                            <li><Link to="/shipping" data-testid="footer-link-shipping" className="hover:text-[var(--hf-text)] transition-colors">Shipping</Link></li>
                            <li><Link to="/faq" data-testid="footer-link-faq" className="hover:text-[var(--hf-text)] transition-colors">FAQ</Link></li>
                            <li><Link to="/about" className="hover:text-[var(--hf-text)] transition-colors">About</Link></li>
                            {extraFooterPages.map((p) => (
                                <li key={p.slug}><Link to={`/${p.slug}`} data-testid={`footer-link-${p.slug}`} className="hover:text-[var(--hf-text)] transition-colors">{p.title}</Link></li>
                            ))}
                        </ul>
                    </div>

                    {/* Social */}
                    <div className="lg:col-span-3">
                        <p className="eyebrow" style={{ color: 'var(--hf-text)' }}>Follow</p>
                        <div className="mt-4 flex items-center gap-4">
                            {social.instagram && (
                                <a href={social.instagram} target="_blank" rel="noreferrer" data-testid="footer-social-instagram-link" aria-label="Instagram" className="hover:opacity-70 transition-opacity">
                                    <Instagram className="w-5 h-5" style={{ color: 'var(--hf-text)' }} />
                                </a>
                            )}
                            {social.facebook && (
                                <a href={social.facebook} target="_blank" rel="noreferrer" data-testid="footer-social-facebook-link" aria-label="Facebook" className="hover:opacity-70 transition-opacity">
                                    <Facebook className="w-5 h-5" style={{ color: 'var(--hf-text)' }} />
                                </a>
                            )}
                            {social.youtube && (
                                <a href={social.youtube} target="_blank" rel="noreferrer" data-testid="footer-social-youtube-link" aria-label="YouTube" className="hover:opacity-70 transition-opacity">
                                    <Youtube className="w-5 h-5" style={{ color: 'var(--hf-text)' }} />
                                </a>
                            )}
                            {social.twitter && (
                                <a href={social.twitter} target="_blank" rel="noreferrer" data-testid="footer-social-twitter-link" aria-label="Twitter" className="hover:opacity-70 transition-opacity">
                                    <Twitter className="w-5 h-5" style={{ color: 'var(--hf-text)' }} />
                                </a>
                            )}
                        </div>
                        {settings?.footer_tagline && (
                            <p className="mt-6 text-xs italic" style={{ color: 'var(--hf-muted)' }}>
                                {settings.footer_tagline}
                            </p>
                        )}
                    </div>
                </div>

                <div className="mt-12 pt-6 border-t border-[rgba(94,33,52,0.12)] flex flex-col md:flex-row items-center justify-between gap-3 text-xs" style={{ color: 'var(--hf-muted)' }}>
                    <p>{settings?.footer_copyright}</p>
                    <p className="eyebrow">{settings?.brand_name || 'by anfal'}</p>
                </div>
            </div>
        </footer>
    );
}
